import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, FileCheck, ChevronLeft, ChevronDown, ChevronUp, Clock, AlertTriangle, CheckCircle2, XCircle, Loader2, Search, Calendar, Upload, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '@/context/AppContext';
import apiService from '@/services/apiService';

const HistoryPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, authLoading, loginWithGoogle } = useApp();
  
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const loadHistory = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      try {
        const data = await apiService.getDocumentHistory();
        setAnalyses(data.analyses || []);
      } catch (error) {
        console.error('Failed to load history:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      loadHistory();
    }
  }, [isAuthenticated, authLoading]);

  const getStatusFromScore = (score) => {
    if (score >= 80) return 'ready';
    if (score >= 50) return 'warning';
    return 'critical';
  };

  const getStatusColor = (score) => {
    if (score >= 80) return 'text-verify bg-verify/10 border-verify/20';
    if (score >= 50) return 'text-warning bg-warning/10 border-warning/20';
    return 'text-destructive bg-destructive/10 border-destructive/20';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-verify';
    if (score >= 50) return 'text-warning';
    return 'text-destructive';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleUploadAgain = (analysis) => {
    // Navigate to upload page with the document type pre-selected
    navigate('/upload', {
      state: {
        selectedDocument: {
          name: analysis.document_type,
          category: analysis.category || null
        },
        visaType: analysis.visa_type || 'Spain Visa'
      }
    });
  };

  const toggleExpand = (documentId) => {
    setExpandedId(expandedId === documentId ? null : documentId);
  };

  // Filter analyses
  const filteredAnalyses = analyses.filter(analysis => {
    const matchesSearch = !searchQuery || 
      analysis.document_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      analysis.filename?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const status = getStatusFromScore(analysis.readiness_score);
    const matchesFilter = filterStatus === 'all' || status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Not authenticated view
  if (!authLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-trust" />
              <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
            </button>
          </div>
        </header>
        
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <FileCheck className="w-16 h-16 text-slate-300 mx-auto mb-6" />
          <h1 className="font-outfit font-bold text-3xl text-slate-900 mb-4">
            Sign in to view your history
          </h1>
          <p className="font-public text-lg text-slate-600 mb-8">
            Keep track of all your document analyses with scores and recommendations.
          </p>
          <button
            onClick={loginWithGoogle}
            className="bg-trust text-trust-foreground px-8 py-4 rounded-md font-public font-semibold text-lg hover:bg-trust/90 active:scale-95 transition-all shadow-md"
            data-testid="sign-in-btn"
          >
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-600 hover:text-trust transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-public">Dashboard</span>
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-trust" />
            <span className="font-outfit font-bold text-xl text-slate-900">Visa-Ready</span>
          </div>
          <div className="w-20" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-outfit font-bold text-3xl md:text-4xl text-slate-900 mb-2">
                Document History
              </h1>
              <p className="font-public text-slate-600">
                View all your analyzed documents from the past 90 days
              </p>
            </div>
            <button
              onClick={() => navigate('/select-country')}
              className="bg-trust text-trust-foreground px-6 py-3 rounded-md font-public font-semibold hover:bg-trust/90 active:scale-95 transition-all shadow-sm hidden md:inline-flex items-center gap-2"
              data-testid="new-analysis-btn"
            >
              <FileCheck className="w-5 h-5" />
              New Analysis
            </button>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by document type or filename..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-md font-public text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-trust/20 focus:border-trust"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'ready', 'warning', 'critical'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-md font-public text-sm capitalize transition-colors ${
                    filterStatus === status
                      ? 'bg-trust text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${status}`}
                >
                  {status === 'all' ? 'All' : status}
                </button>
              ))}
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-trust mx-auto mb-3" />
              <p className="text-slate-500">Loading your history...</p>
            </div>
          ) : filteredAnalyses.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
              <FileCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-outfit font-bold text-lg text-slate-900 mb-2">
                {analyses.length === 0 ? 'No analyses yet' : 'No matching results'}
              </h3>
              <p className="text-slate-500 mb-6">
                {analyses.length === 0 
                  ? 'Start by analyzing your first document'
                  : 'Try adjusting your search or filters'}
              </p>
              {analyses.length === 0 && (
                <button
                  onClick={() => navigate('/select-country')}
                  className="bg-trust text-trust-foreground px-6 py-3 rounded-md font-public font-semibold hover:bg-trust/90 transition-colors"
                >
                  Analyze Documents
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAnalyses.map((analysis, idx) => {
                const isExpanded = expandedId === analysis.document_id;
                const status = getStatusFromScore(analysis.readiness_score);
                
                return (
                  <motion.div
                    key={analysis.document_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`bg-white border rounded-lg overflow-hidden transition-all ${
                      isExpanded ? 'border-trust shadow-md' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`history-item-${idx}`}
                  >
                    {/* Header Row - Always Visible */}
                    <button
                      onClick={() => toggleExpand(analysis.document_id)}
                      className="w-full p-6 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg border ${getStatusColor(analysis.readiness_score)}`}>
                          <FileCheck className="w-6 h-6" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-outfit font-bold text-lg text-slate-900 mb-1">
                            {analysis.document_type}
                          </h3>
                          <p className="text-sm text-slate-500 mb-2">{analysis.filename}</p>
                          <div className="flex flex-wrap items-center gap-3 text-sm">
                            <span className="text-slate-500 flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(analysis.created_at)}
                            </span>
                            {analysis.critical_issues?.length > 0 && (
                              <span className="text-destructive flex items-center gap-1">
                                <XCircle className="w-4 h-4" />
                                {analysis.critical_issues.length} critical
                              </span>
                            )}
                            {analysis.warnings?.length > 0 && (
                              <span className="text-warning flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {analysis.warnings.length} warning{analysis.warnings.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className={`text-3xl font-outfit font-bold ${getScoreColor(analysis.readiness_score)}`}>
                            {analysis.readiness_score}%
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(analysis.readiness_score)}`}>
                            {status}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </button>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="border-t border-slate-200"
                        >
                          <div className="p-6 space-y-6">
                            {/* Critical Issues */}
                            {analysis.critical_issues?.length > 0 && (
                              <div>
                                <h4 className="font-public font-semibold text-destructive mb-3 flex items-center gap-2">
                                  <XCircle className="w-4 h-4" />
                                  Critical Issues ({analysis.critical_issues.length})
                                </h4>
                                <div className="space-y-2">
                                  {analysis.critical_issues.map((issue, i) => (
                                    <div key={i} className="bg-destructive/5 border border-destructive/20 rounded-md p-3">
                                      <p className="font-medium text-slate-900">
                                        {typeof issue === 'string' ? issue : issue.title || issue.description || JSON.stringify(issue)}
                                      </p>
                                      {issue.description && issue.title && (
                                        <p className="text-sm text-slate-600 mt-1">{issue.description}</p>
                                      )}
                                      {issue.recommendation && (
                                        <p className="text-sm text-destructive mt-2 font-medium">
                                          Fix: {issue.recommendation}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Warnings */}
                            {analysis.warnings?.length > 0 && (
                              <div>
                                <h4 className="font-public font-semibold text-warning mb-3 flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4" />
                                  Warnings ({analysis.warnings.length})
                                </h4>
                                <div className="space-y-2">
                                  {analysis.warnings.map((warning, i) => (
                                    <div key={i} className="bg-warning/5 border border-warning/20 rounded-md p-3">
                                      <p className="font-medium text-slate-900">
                                        {typeof warning === 'string' ? warning : warning.title || warning.description || JSON.stringify(warning)}
                                      </p>
                                      {warning.description && warning.title && (
                                        <p className="text-sm text-slate-600 mt-1">{warning.description}</p>
                                      )}
                                      {warning.recommendation && (
                                        <p className="text-sm text-warning mt-2 font-medium">
                                          Suggestion: {warning.recommendation}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Passed Checks */}
                            {analysis.passed_checks?.length > 0 && (
                              <div>
                                <h4 className="font-public font-semibold text-verify mb-3 flex items-center gap-2">
                                  <CheckCircle2 className="w-4 h-4" />
                                  Passed Checks ({analysis.passed_checks.length})
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {analysis.passed_checks.map((check, i) => (
                                    <div key={i} className="bg-verify/5 border border-verify/20 rounded-md p-3">
                                      <p className="font-medium text-slate-900">
                                        {typeof check === 'string' ? check : check.title || check.description || JSON.stringify(check)}
                                      </p>
                                      {check.description && check.title && (
                                        <p className="text-sm text-slate-600 mt-1">{check.description}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* No issues message */}
                            {(!analysis.critical_issues || analysis.critical_issues.length === 0) &&
                             (!analysis.warnings || analysis.warnings.length === 0) &&
                             (!analysis.passed_checks || analysis.passed_checks.length === 0) && (
                              <div className="text-center py-4 text-slate-500">
                                <p>No detailed analysis data available for this document.</p>
                              </div>
                            )}

                            {/* Document Metadata */}
                            <div className="bg-slate-50 rounded-lg p-4">
                              <h4 className="font-public font-semibold text-slate-700 mb-3">Document Details</h4>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-slate-500">Language</p>
                                  <p className="font-medium">{analysis.detected_language || 'Unknown'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Confidence</p>
                                  <p className="font-medium">{analysis.type_confidence || 0}%</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Official Stamps</p>
                                  <p className="font-medium">{analysis.has_official_stamps ? 'Yes' : 'No'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Signatures</p>
                                  <p className="font-medium">{analysis.has_signatures ? 'Yes' : 'No'}</p>
                                </div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-wrap gap-3 pt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUploadAgain(analysis);
                                }}
                                className="bg-trust text-trust-foreground px-5 py-2.5 rounded-md font-public font-semibold hover:bg-trust/90 active:scale-95 transition-all inline-flex items-center gap-2"
                                data-testid={`upload-again-${idx}`}
                              >
                                <RefreshCw className="w-4 h-4" />
                                Upload Again
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate('/select-country');
                                }}
                                className="border border-slate-300 text-slate-700 px-5 py-2.5 rounded-md font-public font-medium hover:bg-slate-50 transition-colors inline-flex items-center gap-2"
                              >
                                <Upload className="w-4 h-4" />
                                New Document
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Stats summary */}
          {!loading && analyses.length > 0 && (
            <div className="mt-8 text-center text-sm text-slate-500">
              Showing {filteredAnalyses.length} of {analyses.length} analyses
              {' • '}
              Data retained for 90 days
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default HistoryPage;
